import { Component, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AppState } from './app.state';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { YouforceHeaderComponent } from './youforce-header/youforce-header.component';
import { ConfirmDialogComponent } from './dialog/confirm-dialog/confirm-dialog.component';
import { YdsSnackbarComponent } from './yds-angular';
import { environment } from '../environments/environment';
import { EntitlementsService } from './services/entitlements.service';
import { TenantOnboardingService } from './services/tenant-onboarding.service';
import { LoginInformationService } from './locallogin/login-information.service';
import { isDevLogoffEnabled, isJobsMenuEnabled, isTestEnvironment } from './shared/environment.util';
import {
  YFHeaderItem,
  YFHeaderMenuItem,
  YFHeaderSubMenuItem,
} from './youforce-header/models/youforce-header-menu-item.model';

/** Matches the group tabs exposed by the Youforce navigation bar web component. */
type HeaderGroup = 'delegation' | 'kbj';

@Component({
  selector: 'app-root',
  imports: [
    YouforceHeaderComponent,
    ConfirmDialogComponent,
    YdsSnackbarComponent,
    RouterOutlet,
    TranslateModule,
  ],
  providers: [AppState],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnDestroy {
  readonly youforceNavigationBarCdnUrl = environment.navigationBarCdnUrl;

  languages = ['en-GB', 'nl-NL'];
  currentLang = 'nl-NL';

  /** Set when the CDN navigation bar web component finishes rendering. */
  readonly isYFHeaderRendered = signal(false);
  /** Culture/locale passed into the Youforce navigation bar (`culture` attribute). */
  readonly yfHeaderCulture = signal('');
  /** Sub-header items for module/delegation navigation inside the org bar. */
  readonly menuItems = signal<YFHeaderSubMenuItem[]>([]);
  readonly helpItems = signal<YFHeaderItem[]>([]);
  readonly activeItemId = signal<string | null>(null);
  readonly headerGroup = signal<HeaderGroup | null>(null);
  readonly headerLoadError = signal<string | null>(null);
  private readonly menuLocaleVersion = signal(0);

  private readonly document = inject<Document>(DOCUMENT);
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly entitlementsService = inject(EntitlementsService);
  private readonly tenantOnboardingService = inject(TenantOnboardingService);
  private readonly loginInformationService = inject(LoginInformationService);
  private bodyStyleObserver: MutationObserver | null = null;

  constructor() {
    this.translate.setFallbackLang(this.currentLang);
    const savedLang = localStorage.getItem('language');
    if (savedLang && this.languages.includes(savedLang)) {
      this.currentLang = savedLang;
    }

    this.translate.use(this.currentLang);

    this.translate.get('App-Journaling.Title').subscribe((title) => {
      this.title.setTitle(title);
    });

    this.document.documentElement.lang = this.translate.getCurrentLang();
    this.yfHeaderCulture.set(this.translate.getCurrentLang());
    this.menuItems.set(this.createMenuItems());
    this.startBodyMarginTopEnforcer();

    // Rebuild the menu when entitlements are loaded or the language changes.
    effect(() => {
      this.menuLocaleVersion();
      if (this.entitlementsService.initialized()) {
        this.menuItems.set(this.createMenuItems());
      }
    });
    
    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
      this.document.documentElement.lang = event.lang;
      this.yfHeaderCulture.set(event.lang);
      this.menuItems.set(this.createMenuItems());
      this.menuLocaleVersion.update((version) => version + 1);
    });

    // Rebuild the menu on navigation so the URL-based preference applies.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.menuItems.set(this.createMenuItems());
      }
    });

    // When the module is registered and configuration is available, initialize app auth here:
    // void this.initAuthentication();
  }

  onYFHeaderRendered(isRendered: boolean): void {
    this.isYFHeaderRendered.set(isRendered);
    this.enforceBodyMarginTop();
  }

  ngOnDestroy(): void {
    this.bodyStyleObserver?.disconnect();
    this.bodyStyleObserver = null;
  }

  onLogoffClicked(): void {
    try {
      if (isDevLogoffEnabled()) {
        this.removeYfSsidCookie();
        this.entitlementsService.clear();
        this.tenantOnboardingService.clear();
        this.loginInformationService.clearCache();
        return;
      }
      // this.authServiceProvider.authService.logout();
    } catch {
      // AuthService is not initialized yet while the module runs standalone in dev.
    }
  }

  private removeYfSsidCookie(): void {
    this.document.cookie = 'YF_SSID=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  }

  onLoadingHeaderError(message: string): void {
    this.headerLoadError.set(message);
    this.headerGroup.set(null);
    this.isYFHeaderRendered.set(false);
  }

  onSubMenuItemClicked(subMenuItem: YFHeaderMenuItem): void {
    this.activeItemId.set(subMenuItem.id);
    this.router.navigateByUrl(subMenuItem.id);

    const currentGroup = this.headerGroup();
    if (currentGroup) {
      // Reserved for group-specific navigation once the module is registered in Youforce.
    }
  }

  private createMenuItems(): YFHeaderSubMenuItem[] {
    const url = this.router.url;

    // No menu until onboarding is confirmed, so tenants that are not onboarded never see it.
    if (!this.tenantOnboardingService.isModuleAvailable()) {
      return [];
    }

    // URL-based preference: /proces with KBJPROCES => Verwerken, /config with KBJCONFIG => Grootboek.
    if (url.startsWith('/proces') && this.entitlementsService.has('KBJPROCES')) {
      return this.createProcesMenu();
    }
    if (url.startsWith('/config') && this.entitlementsService.has('KBJCONFIG')) {
      return this.createConfigMenu();
    }

    // Fallback: entitlement-only in the original check order.
    if (this.entitlementsService.has('KBJPROCES')) {
      return this.createProcesMenu();
    }

    if (this.entitlementsService.has('KBJCONFIG')) {
      return this.createConfigMenu();
    }

    // If the user has neither entitlement, return an empty menu.
    // This should not happen in practice, as the login guard should prevent access to the app without entitlements.
    return [];
  }

  private createProcesMenu(): YFHeaderSubMenuItem[] {
    const items: YFHeaderSubMenuItem[] = [];

    // Only added menu when you have the entitlement
    if (this.entitlementsService.has('KBJPROCES')) {
      items.push(
        {
          id: 'verwerking',
          displayName: 'Verwerking',
          subItems: [
            { id: '/proces/exportprocessing/createbireport', displayName: 'Loonjournaal voor rapportage' },
            { id: '/proces/export/exportgeneralledger/selectpayrolljournal', displayName: 'Loonjournaal voor grootboeksysteem' },
          ],
        },
        {
          id: 'overview',
          displayName: 'Overzicht',
          subItems: [
            { id: '/proces/overviewjournalentry', displayName: 'Overzicht journaalpost' },
          ],          
        },
        {
          id: '/proces/notimplemented',
          displayName: 'Import',
        },
      );

      // Available in local, development, testing and acceptance environments.
      if (isJobsMenuEnabled()) {
        items.push({
          id: '/proces/jobs',
          displayName: 'Job(s)',
        });
      }
    }

    if (isTestEnvironment() && this.entitlementsService.has('KBJCONFIG')) {
      items.push({
        id: '/config/home',
        displayName: 'Ga naar config',
        subItems: []
      });
    }

    return items;
  }

  private createConfigMenu(): YFHeaderSubMenuItem[] {
    const items: YFHeaderSubMenuItem[] = [];

    // Only added menu when you have the entitlement
    if (this.entitlementsService.has('KBJCONFIG')) {
      items.push(
        {
          id: 'general-ledger',
          displayName: 'Grootboek',
          subItems: [
            { id: '/config/generalledger', displayName: 'Grootboek' },
            { id: '/config/generalledgeraccount', displayName: 'Grootboekrekening' },
            { id: '/config/generalledgergroup', displayName: 'Grootboekrekeningengroep' },
            { id: '/config/notimplemented', displayName: 'Overzicht grootboekdefinitie' },
          ],
        },
        {
          id: 'booking-group',
          displayName: 'Boekingsgroep',
          subItems: [
            { id: '/config/bookinggroup', displayName: 'Boekingsgroep' },
            { id: '/config/bookingdefinition', displayName: 'Boekingsdefinitie' },
            { id: '/config/bookingrule', displayName: 'Boekingsregel' },
            { id: '/config/notimplemented', displayName: 'Overzicht boekingsgroep' },
          ],
        },
        {
          id: 'selection',
          displayName: 'Selectie',
          subItems: [
            { id: '/config/employmentselection', displayName: 'Persoon selectie' },
            { id: '/config/conversiontable', displayName: 'Conversietabel' },
            { id: '/config/notimplemented', displayName: 'Overzicht selecties' },
          ],
        },
        {
          id: '/config/home',
          displayName: 'Verdeling',
          subItems: [
            { id: '/config/notimplemented', displayName: 'Loonverdelingsdefinitie' },
            { id: '/config/notimplemented', displayName: 'Herverdelingsdefinitie' },
            { id: '/config/notimplemented', displayName: 'Herverdelingsregel' },
            { id: '/config/notimplemented', displayName: 'Overzicht verdelingen' },
          ],          
        },
        {
          id: '/config/home',
          displayName: 'Uitvoerdefinitie',
          subItems: [
            { id: '/config/notimplemented', displayName: 'Consolidatiedefinitie' },
            { id: '/config/journalprocessdefinition', displayName: 'Verwerkingsdefinitie' },
            { id: '/config/notimplemented', displayName: 'Overzicht uitvoerdefinities' },
          ],          
        },
        {
          id: '/config/home',
          displayName: 'Exportdefinities',
          subItems: [
            { id: '/config/exportdefinition', displayName: 'Exportdefinitie' },
            { id: '/config/exportrubriek', displayName: 'Exportrubriek' },
            { id: '/config/notimplemented', displayName: 'Overzicht exportdefinitie' },
          ],
        },
      );
    }

    if (isTestEnvironment() && this.entitlementsService.has('KBJPROCES')) {
      items.push({
        id: '/proces/home',
        displayName: 'Ga naar proces',
        subItems: []
      });
    }

    return items;
  }

  onHelpMenuItemClicked(helpItem: YFHeaderItem): void {
    // Help actions are handled by the navigation bar web component; hook for app-specific overrides.
    void helpItem;
  }

  
  private startBodyMarginTopEnforcer(): void {
    this.enforceBodyMarginTop();

    const body = this.document.body;
    if (!body || typeof MutationObserver === 'undefined') {
      return;
    }

    this.bodyStyleObserver?.disconnect();
    this.bodyStyleObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          this.enforceBodyMarginTop();
          break;
        }
      }
    });

    this.bodyStyleObserver.observe(body, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  private enforceBodyMarginTop(): void {
    this.document.body?.style.setProperty('margin-top', '0px', 'important');
  }


  /**
   * Initializes JWT/cookie auth for API calls once configuration is available.
   * The CDN navigation bar handles organization SSO independently of this call.
   */
  // private async initAuthentication(): Promise<void> {
  //   const config = await this.configuration.getConfig();
  //   await this.authServiceProvider.init(config.authenticationConfig);
  // }
}
